import qs from 'qs'

import { assert } from 'core/helpers/assert'
import * as R from 'core/helpers/remeda'

/**
 * Returns an error object that can be thrown, after doing its best to glean
 * information from the response text to add to the error object.
 *
 * @param response - The response object. Make sure its stream hasn't already
 * been read yet by .text() or .json().
 */
export const parseErrorResponse = async (response: Response) => {
  // start with at least default error
  const error = {
    status: response?.status ?? 0,
    message: 'Unknown Error',
    peachRequestId: response?.headers?.get?.('X-Peach-Request-Id') || undefined,
  }

  // Only attempt to parse JSON if we have a valid response object
  if (response && typeof response.json === 'function') {
    try {
      const { status, message, ...rest } = await response.json()

      if (status) {
        error.status = status
      }

      if (message) {
        error.message = message
      }

      Object.assign(error, rest)
    } catch {
      // If JSON parsing fails, use the HTTP status text as a fallback
      if (response.statusText) {
        error.message = response.statusText
      }
    }
  }

  return error
}

export type FetchOptions = Omit<RequestInit, 'body'> & {
  url: string
  query?: { [index: string]: any }
  body?: unknown
  withHeaders?: boolean
}

/**
 * This is wrapper around `window.fetch` (but that could be subbed out for
 * something else later) that
 *
 * - returns a promise
 * - resolves to a value
 * - rejects with an error with at least `status` and `message` fields
 * - Parses a `query` option if passed in
 * - Adds default headers
 * - JSON.stringifies body if an object is passed
 * - Gracefully handle 204
 * - Parses JSON, text, and pdf responses
 * - Parses JSON error
 *
 * Note: does not handle authorization or unauthorized handling. That's one level up in peachApi.js
 */
const rawApiCall = async ({ url, headers, body, query, method, withHeaders, ...restOptions }: FetchOptions) => {
  assert(R.isString(url), 'url is required for an API call')

  const fetchOptions: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...restOptions,
  }

  if (!(body instanceof FormData)) {
    fetchOptions.headers!['Content-Type'] = 'application/json'
  }

  if (body instanceof FormData) {
    fetchOptions.body = body
  } else if (!R.isNil(body)) {
    fetchOptions.body = R.isString(body) ? body : JSON.stringify(body)
  }

  let resp: Response | null = null

  try {
    resp = await window.fetch(url + qs.stringify(query, { addQueryPrefix: true, arrayFormat: 'comma' }), fetchOptions)
    if (resp.ok) {
      const contentType = resp.headers.get('Content-Type')
      return (
        resp.status === 204 && withHeaders ? R.pick(resp, ['headers', 'status'])
        : resp.status === 204 ? {}
        : contentType?.includes('json') ? resp.json()
        : contentType?.includes('text') ? resp.text()
        : contentType?.includes('pdf') ? resp.blob()
        : {}
      )
    }
    throw resp
  } catch (error) {
    console.error('Error fetching', {
      error,
      errorMessage:
        error instanceof Error ? error.message
        : error && typeof error === 'object' && 'status' in error ?
          `HTTP ${(error as any).status} ${(error as any).statusText}`
        : String(error),
      errorStack: error instanceof Error ? error.stack : 'No stack available',
      responseStatus: error && typeof error === 'object' && 'status' in error ? error.status : undefined,
      responseStatusText: error && typeof error === 'object' && 'statusText' in error ? error.statusText : undefined,
      responseHeaders:
        error && typeof error === 'object' && 'headers' in error && error.headers && typeof error.headers === 'object' ?
          'entries' in error.headers ?
            Object.fromEntries((error.headers as any).entries())
          : undefined
        : undefined,
      url,
      query,
      body,
    })

    // If we have a valid Response object (HTTP error), parse it and convert to an `Error` instance
    if (resp && typeof resp === 'object' && typeof resp.json === 'function') {
      const parsed = await parseErrorResponse(resp)

      // Convert the plain object into an `Error` so that tools like Sentry
      // display the `message` instead of the generic "Object captured as promise rejection".
      const err = new Error(parsed.message)
      // Safely copy only non-reserved properties from the parsed error object to avoid
      // clobbering built-in `Error` fields like `stack`, `name`, or `message`.
      const reservedErrorKeys = new Set(['name', 'stack', 'message', 'cause', 'constructor'])

      for (const [key, value] of Object.entries(parsed)) {
        if (!reservedErrorKeys.has(key)) {
          ;(err as any)[key] = value
        }
      }
      throw err
    }

    // For network errors or other non-Response errors, still create a proper `Error` instance
    const networkErr = new Error(error instanceof Error ? error.message : 'Network request failed')
    Object.assign(networkErr, {
      status: 0,
      peachRequestId: undefined,
    })
    throw networkErr
  }
}

export default rawApiCall
